import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, Library, Flame, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { FreeChatPanel } from '../components/app/FreeChatPanel';

export const WelcomePage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [showGuestChat, setShowGuestChat] = useState(false);

  useEffect(() => {
    // If user is logged in, redirect to /today (app dashboard)
    if (user) navigate('/today', { replace: true });
  }, [user, navigate]);

  // Show spinner while checking auth — prevents flash of WelcomePage
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6">
        <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" />
        <p className="mt-6 text-center text-sm text-slate-400 max-w-xs leading-relaxed">
          Przygotowujemy PromptOrigin dla Ciebie. Ze względu na złożoność platformy ładowanie może potrwać chwilę.
        </p>
      </div>
    );
  }

  // Already redirecting
  if (user) return null;

  return (
    <div className="min-h-screen flex flex-col" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 60px)', background: 'transparent' }}>
      <div className="flex-1 flex flex-col items-center px-6 pt-6 pb-4 text-center">
        <div className="animate-slideUp">
          <p className="text-sm font-medium text-slate-400 tracking-wide mb-4">PromptOrigin</p>
          
          <div className="logoWrap w-20 h-20 mx-auto mb-6" data-robot-target="logo">
            <div className="logoCoin w-full h-full rounded-2xl bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center shadow-lg shadow-sky-500/30">
              <BookOpen className="w-10 h-10 text-white" style={{ position: 'relative', zIndex: 10, backfaceVisibility: 'hidden' }} />
              {/* Gold edge layers - 20 dense layers for thick solid coin edge */}
              {[...Array(20)].map((_, i) => (
                <span key={i} className="coinEdge" style={{ transform: `translateZ(-${(i + 1) * 0.4}px)` }}></span>
              ))}
              {/* Back face with AI text */}
              <span className="coinBack">AI</span>
            </div>
          </div>
          
          <h1 className="text-4xl font-bold text-white mb-2">
            Używaj AI jak specjalista.
          </h1>
          <h2 className="text-2xl font-semibold text-sky-500 mb-5">
            Otwórz nowe możliwości.
          </h2>
          
          <p className="text-sm text-white/80 text-center mb-5 max-w-sm mx-auto" style={{ lineHeight: '1.6' }}>
            Platforma codziennego rozwoju AI.<br />
            Od podstaw do poziomu profesjonalnego.
          </p>
          
          {/* Main CTA - directly under headline */}
          <div className="w-full max-w-sm mx-auto mb-6">
            <button
              onClick={() => navigate('/auth')}
              className="w-full h-14 rounded-full bg-sky-500 hover:bg-sky-600 text-white text-lg font-semibold shadow-lg hover:shadow-xl btn-press"
              data-testid="start-btn"
            >
              Zacznij za darmo
            </button>
            <p className="text-sm text-slate-500 text-center mt-3">
              Pierwsze 3 dni bezpłatnie
            </p>
          </div>

          {/* Swobodna rozmowa — pill button style */}
          <div className="w-full max-w-sm mx-auto mb-8">
            <button
              onClick={() => setShowGuestChat(true)}
              className="w-full h-14 rounded-full text-white font-semibold shadow-lg hover:shadow-xl btn-press transition-all duration-200 hover:brightness-110"
              style={{
                background: 'linear-gradient(135deg, #10b981, #0d9488)',
                boxShadow: '0 4px 20px rgba(16,185,129,0.3)',
                fontSize: '0.95rem',
              }}
              data-testid="welcome-free-chat-card"
            >
              Swobodna rozmowa ze sztuczną inteligencją
            </button>
          </div>
          
          {/* Download button */}
          <div className="flex flex-col items-center mb-8">
            <button
              onClick={() => navigate('/download')}
              className="h-12 px-6 rounded-full font-semibold btn-press transition-all duration-300 hover:scale-105"
              style={{ 
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                backdropFilter: 'blur(8px)',
              }}
              data-testid="download-btn-top"
            >
              <span className="flex items-center gap-2">
                <Download className="w-5 h-5 text-sky-400" />
                <span className="text-white/90 text-sm font-medium">Pobierz aplikację</span>
              </span>
            </button>
            
            <p className="text-slate-500 mt-3 text-center text-sm leading-relaxed">
              15 minut dziennie.<br />
              Jedno zadanie.<br />
              Umiejętność, która się opłaca.
            </p>
          </div>
          
          <div className="space-y-3 mb-8">
            <button onClick={() => navigate(user ? '/today' : '/auth')} className="w-full flex items-center gap-3 text-left rounded-xl p-4 transition-all active:scale-[0.98] hover:brightness-125" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }} data-testid="feature-card-1">
              <div className="w-10 h-10 rounded-full bg-sky-500/20 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-sky-500" />
              </div>
              <div>
                <p className="font-semibold text-white">Więcej możliwości</p>
                <p className="text-sm text-slate-400">Nowe zlecenia, lepsza praca, nowe kierunki.</p>
              </div>
            </button>
            
            <button onClick={() => navigate(user ? '/today' : '/auth')} className="w-full flex items-center gap-3 text-left rounded-xl p-4 transition-all active:scale-[0.98] hover:brightness-125" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }} data-testid="feature-card-2">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Library className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="font-semibold text-white">AI, które zarabia</p>
                <p className="text-sm text-slate-400">Używasz go tam, gdzie inni jeszcze nie potrafią.</p>
              </div>
            </button>
            
            <button onClick={() => navigate(user ? '/today' : '/auth')} className="w-full flex items-center gap-3 text-left rounded-xl p-4 transition-all active:scale-[0.98] hover:brightness-125" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }} data-testid="feature-card-3">
              <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                <Flame className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="font-semibold text-white">Przewaga na rynku</p>
                <p className="text-sm text-slate-400">Skill, który robi różnicę w pieniądzach.</p>
              </div>
            </button>
          </div>
          
          <p className="text-center text-slate-600 mb-4 max-w-sm text-sm quote-fade-cycle" style={{ willChange: 'opacity' }}>
            Sztuczna Inteligencja nie zabiera pracy.<br />
            Daje ją tym, którzy umieją jej używać.
          </p>
        </div>
      </div>

      {showGuestChat && (
        <FreeChatPanel onClose={() => setShowGuestChat(false)} guestMode={true} />
      )}
    </div>
  );
};
